import React, { useState, useMemo, useRef, useEffect, useCallback, Suspense } from "react";
import {
  PlusCircleIcon,
  BellIcon,
  PencilIcon,
  TrashIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions, ComboboxButton } from "@headlessui/react";
import { format } from "date-fns";
import { XIcon } from "lucide-react";
import { type MyLatestReminderList, type User } from "../types";
import { fakeUsers, matters as matterList } from "../../mocks/calendarMocks";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/components/ui/use-toast";
import { useLocation, useNavigate, useSearchParams } from "react-router";

export interface EventTableProps {
  eventsAndReminders: MyLatestReminderList["reminderList"];
  onAddSubtask: () => void;
  onSetReminder: () => void;
  onEditEvent: () => void;
  onDeleteEvent: () => void;
}

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

const UserFilter: React.FC<{
  selectedUsers: Array<User>;
  setSelectedUsers: React.Dispatch<React.SetStateAction<Array<User>>>;
}> = ({ selectedUsers, setSelectedUsers }) => {
  const [searchQuery, setSearchQuery] = useState<string>("");

  const comboboxRef = useRef<HTMLDivElement | null>(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (comboboxRef.current && !comboboxRef.current.contains(event.target as Node)) {
      setSearchQuery("");
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const filteredUsers = useMemo(() => {
    if (!searchQuery) return fakeUsers;
    return fakeUsers.filter(user => user.name.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [searchQuery]);

  const handleRemoveUser = useCallback(
    (userId: string) => {
      setSelectedUsers(prevSelectedUsers => prevSelectedUsers.filter(user => user.id !== userId));
    },
    [setSelectedUsers],
  );

  return (
    <div className="flex items-center">
      <Combobox multiple value={selectedUsers} onChange={setSelectedUsers} onClose={() => setSearchQuery("")}>
        <div className="relative mr-2" ref={comboboxRef}>
          <ComboboxInput
            className="w-72 rounded-md border border-gray-300 py-2 pl-3 pr-10 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm"
            displayValue={(users: User[]) => users.map(user => user.name).join(", ")}
            onChange={event => setSearchQuery(event.target.value)}
            placeholder="Search and select users"
          />
          <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronDownIcon className="h-5 w-5 text-gray-400" />
          </ComboboxButton>

          <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
            {filteredUsers.length === 0 && searchQuery !== "" ? (
              <div className="relative cursor-default select-none px-4 py-2 text-gray-700">No users found.</div>
            ) : (
              filteredUsers.map(user => (
                <ComboboxOption
                  key={user.id}
                  value={user}
                  className={({ active }) =>
                    classNames(
                      "relative cursor-default select-none py-2 pl-10 pr-4",
                      active ? "bg-indigo-600 text-white" : "text-gray-900",
                    )
                  }
                >
                  {({ selected, active }) => (
                    <>
                      <span className={classNames("block truncate", selected ? "font-medium" : "font-normal")}>
                        {user.name}
                      </span>
                      {selected ? (
                        <span
                          className={classNames(
                            "absolute inset-y-0 left-0 flex items-center pl-3",
                            active ? "text-white" : "text-indigo-600",
                          )}
                        >
                          <svg
                            className="h-5 w-5"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            aria-hidden="true"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 00-1.414 0L7 13.586l-3.293-3.293a1 1 0 00-1.414 1.414l4 4a1 1 0 001.414 0l9-9a1 1 0 000-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </span>
                      ) : null}
                    </>
                  )}
                </ComboboxOption>
              ))
            )}
          </ComboboxOptions>
        </div>
      </Combobox>
      <div className="mt-2 flex flex-wrap">
        {selectedUsers.map(user => (
          <div key={user.id} className="mb-2 mr-2 flex items-center rounded-full bg-gray-200 px-3 py-1">
            <img
              className="inline-block h-5 w-5 rounded-full ring-2 ring-white"
              src={user.avatar}
              alt={user.name}
              width={20}
              height={20}
            />
            <span className="ml-2 text-sm text-gray-700">{user.name}</span>
            <XIcon className="ml-2 h-4 w-4 cursor-pointer text-gray-600" onClick={() => handleRemoveUser(user.id)} />
          </div>
        ))}
      </div>
    </div>
  );
};

const MatterFilter: React.FC<{
  selectedMatters: Array<string>;
  setSelectedMatters: React.Dispatch<React.SetStateAction<Array<string>>>;
}> = ({ selectedMatters, setSelectedMatters }) => {
  const [searchQuery, setSearchQuery] = useState<string>("");

  const comboboxRef = useRef<HTMLDivElement | null>(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (comboboxRef.current && !comboboxRef.current.contains(event.target as Node)) {
      setSearchQuery("");
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const filteredMatters = useMemo(() => {
    if (!searchQuery) return matterList;
    return matterList.filter(
      matter =>
        matter.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
        matter.title.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [searchQuery]);

  const handleRemoveMatter = useCallback(
    (matterId: string) => {
      setSelectedMatters(prevSelectedMatters => prevSelectedMatters.filter(id => id !== matterId));
    },
    [setSelectedMatters],
  );

  return (
    <div className="flex items-center">
      <Combobox
        multiple
        value={selectedMatters}
        onChange={values => {
          if (values.length <= 2) {
            setSelectedMatters(values);
          }
        }}
        onClose={() => setSearchQuery("")}
      >
        <div className="relative mr-2" ref={comboboxRef}>
          <ComboboxInput
            className="w-72 rounded-md border border-gray-300 py-2 pl-3 pr-10 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm"
            displayValue={(matters: string[]) =>
              matters
                .map(id => {
                  const matter = matterList.find(m => m.id === id);
                  return matter ? matter.title : id;
                })
                .join(", ")
            }
            onChange={event => setSearchQuery(event.target.value)}
            placeholder="Search and select matter IDs"
          />
          <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronDownIcon className="h-5 w-5 text-gray-400" />
          </ComboboxButton>

          <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
            {filteredMatters.length === 0 && searchQuery !== "" ? (
              <div className="relative cursor-default select-none px-4 py-2 text-gray-700">No matters found.</div>
            ) : (
              filteredMatters.map(matter => (
                <ComboboxOption
                  key={matter.id}
                  value={matter.id}
                  className={({ active }) =>
                    classNames(
                      "relative cursor-default select-none py-2 pl-10 pr-4",
                      active ? "bg-indigo-600 text-white" : "text-gray-900",
                    )
                  }
                >
                  {({ selected, active }) => (
                    <>
                      <span className={classNames("block truncate", selected ? "font-medium" : "font-normal")}>
                        {matter.title}
                      </span>
                      {selected ? (
                        <span
                          className={classNames(
                            "absolute inset-y-0 left-0 flex items-center pl-3",
                            active ? "text-white" : "text-indigo-600",
                          )}
                        >
                          <svg
                            className="h-5 w-5"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            aria-hidden="true"
                          >
                            <path
                              fillRule="evenodd"
                              d="M5 9a1 1 0 011-1h8a1 1 0 010 2H6a1 1 0 01-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </span>
                      ) : null}
                    </>
                  )}
                </ComboboxOption>
              ))
            )}
          </ComboboxOptions>
        </div>
      </Combobox>
      <div className="mt-2 flex flex-wrap">
        {selectedMatters.map(matterId => (
          <div key={matterId} className="mb-2 mr-2 flex items-center rounded-full bg-gray-200 px-3 py-1">
            <span className="ml-2 text-sm text-gray-700">
              {matterList.find(matter => matter.id === matterId)?.title || matterId}
            </span>
            <XIcon className="ml-2 h-4 w-4 cursor-pointer text-gray-600" onClick={() => handleRemoveMatter(matterId)} />
          </div>
        ))}
      </div>
    </div>
  );
};

const EventTableTeam: React.FC<EventTableProps> = ({
  eventsAndReminders,
  onAddSubtask,
  onSetReminder,
  onEditEvent,
  onDeleteEvent,
}) => {
  const [searchParams] = useSearchParams();
  const [selectedUsers, setSelectedUsers] = useState<Array<User>>([]);
  const [selectedMatters, setSelectedMatters] = useState<Array<string>>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [sortField, setSortField] = useState<string>("datetime");
  const [sortOrder, setSortOrder] = useState<string>("asc");
  const pageSize = 15;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { toast } = useToast();

  useEffect(() => {
    const matterIdParams = searchParams.getAll("matterId");
    if (matterIdParams.length) {
      setSelectedMatters(matterIdParams);
    }

    const showToastParam = searchParams.get("showToast");
    if (showToastParam === "true") {
      toast({
        description: "Your reminders have been set",
        title: "Congratulations",
        variant: "success",
      });

      const newSearchParams = new URLSearchParams(searchParams.toString());
      newSearchParams.delete("showToast");

      navigate(`${pathname}?${newSearchParams.toString()}`);
    }
  }, [searchParams, navigate, toast, pathname]);

  const filteredEventsAndReminders = useMemo(() => {
    const selectedUserIds = selectedUsers.map(user => user.id);
    return eventsAndReminders
      .filter(item => {
        const userMatch =
          selectedUsers.length === 0 || item.assignedUserIds.some(userId => selectedUserIds.includes(userId));
        const matterMatch = selectedMatters.length === 0 || selectedMatters.includes(item.matterId);
        const searchMatch =
          !searchQuery ||
          item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.summary.toLowerCase().includes(searchQuery.toLowerCase()) ||
          format(new Date(item.datetime), "dd/MM/yyyy, EEEE").toLowerCase().includes(searchQuery.toLowerCase()) ||
          matterList
            .find(m => m.id === item.matterId)
            ?.title.toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          item.assignedUserIds.some(userId =>
            fakeUsers
              .find(user => user.id === userId)
              ?.name.toLowerCase()
              .includes(searchQuery.toLowerCase()),
          );
        return userMatch && matterMatch && searchMatch;
      })
      .sort((a, b) => {
        let comparison = 0;
        if (sortField === "datetime") {
          const dateA = new Date(a.datetime);
          const dateB = new Date(b.datetime);
          comparison = dateA.getTime() - dateB.getTime();
        } else {
          const fieldA = a[sortField as keyof typeof a];
          const fieldB = b[sortField as keyof typeof b];
          if (typeof fieldA === "string" && typeof fieldB === "string") {
            comparison = fieldA.localeCompare(fieldB);
          }
        }
        return sortOrder === "asc" ? comparison : -comparison;
      });
  }, [eventsAndReminders, selectedUsers, selectedMatters, searchQuery, sortField, sortOrder]);

  const paginatedEventsAndReminders = useMemo(() => {
    const start = currentPage * pageSize;
    return filteredEventsAndReminders.slice(start, start + pageSize);
  }, [filteredEventsAndReminders, currentPage, pageSize]);

  const pageCount = Math.ceil(filteredEventsAndReminders.length / pageSize);

  const handlePageChange = (page: number) => {
    if (page >= 0 && page < pageCount) {
      setCurrentPage(page);
    }
  };

  const handleClearFilters = () => {
    setSelectedUsers([]);
    setSelectedMatters([]);
    setSearchQuery("");
    navigate(`${pathname}`);
  };

  const handleSortChange = (field: string) => {
    if (sortField === field) {
      setSortOrder(prevOrder => (prevOrder === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const renderRow = (item: MyLatestReminderList["reminderList"][0]) => (
    <TableRow
      key={item.id}
      className={classNames(item.type === "event" ? "bg-gray-100 bg-opacity-50" : "bg-violet-100 bg-opacity-60")}
    >
      <TableCell className="px-4 py-4">
        {format(new Date(item.datetime), "dd/MM/yyyy, EEEE")}
        <br /> <span className="text-xs text-gray-500">{item.location}</span>
      </TableCell>
      <TableCell className="w-1/3 px-4 py-4">
        <div className="text-md font-bold">{matterList.find(m => m.id === item.matterId)?.title}</div>
        {item.title}
      </TableCell>
      <TableCell className="px-4 py-4">{item.summary}</TableCell>
      <TableCell className="px-4 py-4">
        <div className="flex space-x-2">
          {item.assignedUserIds.map(userId => {
            const user = fakeUsers.find(u => u.id === userId);
            return user ? (
              <div key={userId} className="flex items-center space-x-2">
                <img
                  className="inline-block h-8 w-8 rounded-full ring-2 ring-white"
                  src={user.avatar}
                  alt={user.name}
                  width={32}
                  height={32}
                />
              </div>
            ) : null;
          })}
        </div>
      </TableCell>
      <TableCell className="px-4 py-2">
        <div className="flex items-center justify-center space-x-2">
          <PlusCircleIcon className="h-6 w-6 cursor-pointer text-gray-500" onClick={onAddSubtask} />
          <BellIcon className="h-6 w-6 cursor-pointer text-gray-500" onClick={onSetReminder} />
          <PencilIcon className="h-6 w-6 cursor-pointer text-gray-500" onClick={onEditEvent} />
          <TrashIcon className="h-6 w-6 cursor-pointer text-gray-500" onClick={onDeleteEvent} />
        </div>
      </TableCell>
    </TableRow>
  );

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <UserFilter selectedUsers={selectedUsers} setSelectedUsers={setSelectedUsers} />
        <MatterFilter selectedMatters={selectedMatters} setSelectedMatters={setSelectedMatters} />
        <button
          className="ml-4 rounded-md bg-red-600 px-4 py-2 text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          onClick={handleClearFilters}
        >
          Clear All Filters
        </button>
      </div>
      <div className="mb-4 flex items-center">
        <input
          type="text"
          className="w-full rounded-md border border-gray-300 py-2 pl-3 pr-10 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm"
          placeholder="Search by person, date, title, summary, or matter title"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
      </div>
      <Table className="min-w-full bg-white">
        <TableHeader>
          <TableRow>
            <TableHead className="w-1/6 cursor-pointer px-4 py-2" onClick={() => handleSortChange("datetime")}>
              Date {sortField === "datetime" && (sortOrder === "asc" ? "↑" : "↓")}
            </TableHead>
            <TableHead className="w-1/3 px-4 py-2">Title</TableHead>
            <TableHead className="w-1/3 px-4 py-2">Summary</TableHead>
            <TableHead className="w-1/6 px-4 py-2">People Assigned</TableHead>
            <TableHead className="w-1/6 px-4 py-2">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {paginatedEventsAndReminders.length ? (
            paginatedEventsAndReminders.map(item => renderRow(item))
          ) : (
            <TableRow>
              <TableCell colSpan={5} className="h-24 text-center">
                No results.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <div className="mt-4 flex items-center justify-between">
        <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 0}
            className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
          >
            <span className="sr-only">Previous</span>
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
          </button>
          {[...Array<number>(pageCount)].map((_, index) => (
            <button
              key={index}
              onClick={() => handlePageChange(index)}
              className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 ${
                currentPage === index ? "z-10 bg-indigo-600 text-white" : "text-gray-900"
              }`}
            >
              {index + 1}
            </button>
          ))}
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage >= pageCount - 1}
            className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
          >
            <span className="sr-only">Next</span>
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </nav>
      </div>
    </div>
  );
};

const EventTableSuspenseWrapper: React.FC<EventTableProps> = props => (
  <Suspense fallback={<div>Loading...</div>}>
    <EventTableTeam {...props} />
  </Suspense>
);

export default EventTableSuspenseWrapper;
